import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  HeartHandshake,
  Sprout,
  ShieldCheck,
  Users,
  Quote,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import BCorpMark from "@/components/BCorpMark";
import { fadeUp, stagger, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";

// Placeholder hero — team photo stands in for the not-yet-final campaign
// "team celebrating certification" shot. Swap when the real image lands.
const HERO_IMG = "https://cdn.candopayments.com/team/team_colour_web_solid.jpg";
const LIVING_WAGE = "https://cdn.candopayments.com/certifications/living-wage-employer_colour_web_transparent.png";
const FOUNDERS_IMG = "https://cdn.candopayments.com/team/gary-sam_colour_web_solid.jpg";

const HERO_PARAS = [
  "CanDo Payments is a Certified B Corporation™ and an Accredited Living Wage Employer — independent proof of how we've always tried to run this business: transparent, fair and genuinely people-first.",
  "We help UK businesses take payments simply and securely, while proving that competitive pricing, great service and responsible business can go together.",
];

const PROOF = [
  "Certified B Corporation",
  "Accredited Living Wage Employer",
  "Family-run since 2018",
  "UK-based human support",
];

const IN_SHORT =
  "CanDo Payments is a UK payment provider — card machines, EPOS, gateways, online payments and Pay by Link — built around transparency, fair treatment and long-term relationships. Being a B Corp™ and Living Wage employer is how we prove it, not just say it.";

const PILLARS = [
  {
    num: "01",
    icon: Briefcase,
    title: "Better for business",
    body: "Confident payments, lower costs and the right technology for how you trade — card machines, EPOS, gateways, online payments and Pay by Link. Price matters, but so do trust, clear communication and doing what we said we would.",
  },
  {
    num: "02",
    icon: HeartHandshake,
    title: "Better for people",
    body: "A family-run business founded by Gary and Sam Cameron, shaped by years in hospitality — where service is about how you make people feel. Our Living Wage accreditation is part of that: fair pay for the people who build the business.",
  },
  {
    num: "03",
    icon: Sprout,
    title: "Better for the future",
    body: "B Corp isn't about being perfect — it's about being measured, challenged and willing to improve as we grow. For us, this is not the finish line.",
  },
];

const BCORP_AREAS = ["Governance", "Workers", "Customers", "Community", "Suppliers", "Environment", "Accountability", "Decision-making"];

const WHO_FOR = [
  "A small business owner looking for a payment provider you can trust",
  "A hospitality, retail or service business reviewing your payment costs",
  "An ecommerce business that wants better payment support",
  "A B Corp or values-led business looking for aligned suppliers",
  "A partner, developer or agency that wants a responsible provider",
  "A future team member who wants to know what CanDo stands for",
];

const AT_A_GLANCE = [
  { label: "Accreditations", value: "Certified B Corporation & Accredited Living Wage Employer" },
  { label: "Best for", value: "SMEs that want clear pricing, real support and responsible values" },
  { label: "Services", value: "Card machines, EPOS, payment gateways, online payments, Pay by Link" },
  { label: "Support", value: "UK-based, human support from a family-run team" },
];

const BEGINNING = [
  "We are proud that CanDo Payments is a Certified B Corporation. We are proud to be an Accredited Living Wage Employer. But we are even more proud of what those milestones represent.",
  "They represent the customers who trusted us. The team who helped build the business. The partners who supported us. The values that shaped us. And the responsibility we carry into the future.",
  "Purpose Beyond Payments is not a one-week campaign. It is the way we want to keep building CanDo Payments.",
];

const FAQS = [
  { q: "What does it mean that CanDo Payments is a Certified B Corporation?", a: "It means CanDo Payments has been independently assessed against B Corp standards covering areas such as governance, workers, customers, community, environment and accountability. For us, it reflects our commitment to building a responsible, transparent and people-focused payment business." },
  { q: "What is a B Corp payment provider?", a: "A B Corp payment provider is a payment services business that has achieved B Corp certification. This means the business has been assessed against recognised standards for social and environmental responsibility, accountability and transparency." },
  { q: "Why did CanDo Payments become a B Corp?", a: "CanDo Payments became a B Corp because we wanted an independent benchmark for the way we operate. We wanted to prove that our values show up in the running of the business, not just in what we say publicly." },
  { q: "Is CanDo Payments an Accredited Living Wage Employer?", a: "Yes. CanDo Payments is an Accredited Living Wage Employer. This reflects our belief that fair pay matters and that the people helping build the business should be treated with respect." },
  { q: "Does working with a B Corp payment provider cost more?", a: "Not necessarily. CanDo Payments still focuses on helping businesses access competitive pricing, clear advice and practical payment solutions. Our belief is that businesses should not have to choose between value, service and responsible business." },
  { q: "What types of businesses does CanDo Payments support?", a: "CanDo Payments supports UK SMEs across sectors including hospitality, retail, professional services, health and beauty, trades, ecommerce and service-based businesses." },
  { q: "What payment services does CanDo Payments provide?", a: "CanDo Payments provides card payment machines, EPOS systems, payment gateways, online payment solutions, Pay by Link, recurring payment support and partner referral opportunities." },
  { q: "Why should customers care about B Corp certification?", a: "Customers increasingly want to know who they are working with. B Corp certification gives extra reassurance that a business has been independently assessed and is serious about accountability, transparency and continuous improvement." },
  { q: "What does Purpose Beyond Payments mean?", a: "Purpose Beyond Payments is CanDo Payments' campaign platform. It means helping businesses take payments confidently while building the company around three commitments: better for business, better for people and better for the future." },
  { q: "How can I speak to CanDo Payments?", a: "You can contact CanDo Payments through the website, call the team on 0800 368 2989 or email hello@candopayments.com." },
];

export default function PurposeBeyondPaymentsPage() {
  return (
    <div>
      <SEOHead
        title="Purpose Beyond Payments Initiative"
        description="Discover our Purpose Beyond Payments initiative. At CanDo Payments, we believe in giving back to the community and supporting meaningful charitable causes."
        path="/purpose-beyond-payments"
        ogTitle="Purpose Beyond Payments | CanDo Payments"
        ogDescription="CanDo Payments is proud to be a Certified B Corporation and Accredited Living Wage Employer. Discover why responsible business matters in payments."
        service={{
          name: "Purpose Beyond Payments",
          description: "Responsible payment services for UK SMEs from a Certified B Corporation and Accredited Living Wage Employer — card machines, EPOS systems, payment gateways, online payments and Pay by Link.",
          serviceType: "Merchant Services",
        }}
        faqs={FAQS}
      />

      {/* Hero — image + dual badges */}
      <section className="hero-gradient py-16 md:py-24" data-testid="pbp-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-4">Certified B Corporation™ · Accredited Living Wage Employer</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl font-bold text-brand-black tracking-tight leading-[1.05] mb-5">
                Purpose Beyond <span className="text-brand-aqua">Payments</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="font-heading text-xl sm:text-2xl font-bold text-brand-black leading-snug mb-6">
                Better for business. Better for people. Better for the future.
              </motion.p>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">{HERO_PARAS[1]}</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#commitments" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="pbp-cta-learn">See what it means <ArrowRight size={16} /></a>
                <Link to="/contact" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors">Talk to us</Link>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeUp} className="relative">
              <img src={HERO_IMG} alt="The CanDo Payments team celebrating B Corp certification and Living Wage accreditation" width="3905" height="2782" className="rounded-3xl shadow-xl w-full h-auto object-cover" />
              {/* Certification seal — prominent focal lockup over the imagery */}
              <div className="absolute -bottom-6 left-4 sm:-left-6 bg-white rounded-2xl shadow-xl px-6 py-5 flex items-center gap-5">
                <BCorpMark variant="black" heightClassName="h-20 sm:h-24" testId="pbp-hero-bcorp-mark" />
                <span className="w-px h-16 bg-brand-light-grey" />
                <img src={LIVING_WAGE} alt="Accredited Living Wage Employer" width="825" height="678" className="h-14 sm:h-16 w-auto" />
              </div>
              <span className="absolute top-3 right-3 bg-white/85 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-brand-grey px-2.5 py-1 rounded-full">Placeholder image</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Proof band */}
      <section className="py-6 bg-brand-black" data-testid="pbp-proof">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {PROOF.map((item) => (
            <div key={item} className="flex items-center justify-center gap-2 text-sm text-white/90">
              <ShieldCheck size={16} className="text-brand-aqua flex-shrink-0" />{item}
            </div>
          ))}
        </div>
      </section>

      {/* In short */}
      <section className="py-14 md:py-16 bg-white" data-testid="pbp-in-short">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-4">In short</p>
          <p className="text-xl sm:text-2xl text-brand-black font-heading leading-snug">{IN_SHORT}</p>
        </div>
      </section>

      {/* Three commitments — the spine */}
      <section id="commitments" className="py-16 md:py-20 bg-gray-50 scroll-mt-24" data-testid="pbp-commitments">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">What Purpose Beyond Payments means</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Three commitments</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((pillar) => (
              <motion.div key={pillar.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-7 shadow-sm border-t-4 border-t-brand-aqua">
                <div className="flex items-center justify-between mb-4">
                  <pillar.icon size={26} className="text-brand-aqua" />
                  <span className="font-heading text-2xl font-bold text-brand-light-grey">{pillar.num}</span>
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{pillar.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{pillar.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* You shouldn't have to choose — punchy value band + mid CTA */}
      <section className="py-16 md:py-20 bg-brand-aqua-light" data-testid="pbp-choose">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-5">You shouldn&apos;t have to choose</h2>
          <p className="text-lg text-brand-black/80 leading-relaxed mb-8">
            Competitive pricing. Real support when something breaks. A provider that operates responsibly. Most businesses assume they can only pick two — <strong className="text-brand-black">with CanDo, you get all three.</strong> Our accreditations mean the way we work has been independently checked, not just claimed.
          </p>
          <Link to="/switch-and-save#calculator" className="inline-flex items-center gap-2 cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm transition-all" data-testid="pbp-cta-mid">
            Review your payment costs <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Why we became a B Corp — areas grid leads */}
      <section className="py-16 md:py-20 bg-white" data-testid="pbp-why-bcorp">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Why we became a B Corp</motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-5">A benchmark, not a badge</motion.h2>
              <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed mb-4">We didn&apos;t become a B Corp for a badge. We wanted a benchmark — independent proof that our values show up in how the business actually runs. The process examined every part of it:</motion.p>
              <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed">Some of it made us proud; some made us think harder. That was the point. It isn&apos;t a claim to be perfect — it&apos;s a commitment to stay accountable as we grow.</motion.p>
            </motion.div>
            <motion.ul initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-2 gap-3">
              {BCORP_AREAS.map((area) => (
                <motion.li key={area} variants={fadeUp} className="flex items-center gap-2 bg-gray-50 border border-brand-light-grey rounded-xl px-4 py-3 text-sm font-semibold text-brand-black">
                  <ShieldCheck size={15} className="text-brand-aqua flex-shrink-0" />{area}
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* Who this page is for */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="pbp-who-for">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="mb-8">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Who it&apos;s for</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight">Especially relevant if you are</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHO_FOR.map((item) => (
              <motion.div key={item} variants={fadeUp} className="flex items-start gap-3 bg-white border border-brand-light-grey rounded-xl p-5">
                <Users size={18} className="text-brand-aqua flex-shrink-0 mt-0.5" />
                <span className="text-sm text-brand-black leading-relaxed">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What we do — compact link chips */}
      <section className="py-14 md:py-16 bg-white" data-testid="pbp-what-we-do">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Still commercial — just done properly</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-6">Practical payment support for UK businesses</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Card machines", to: "/card-machines" },
              { label: "EPOS systems", to: "/epos-systems" },
              { label: "Payment gateway", to: "/payment-gateway" },
              { label: "Online payments", to: "/online-payments" },
              { label: "Partner programme", to: "/partner-programme" },
            ].map((chip) => (
              <Link key={chip.to} to={chip.to} className="inline-flex items-center gap-1.5 bg-gray-50 hover:bg-brand-aqua-light border border-brand-light-grey rounded-full px-5 py-2.5 text-sm font-semibold text-brand-black transition-colors">
                {chip.label}
                <ArrowRight size={14} className="text-brand-aqua" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gary — pull quote */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="pbp-quote">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} className="order-1 lg:order-none">
              <img src={FOUNDERS_IMG} alt="Gary and Sam Cameron, founders of CanDo Payments" width="2832" height="4240" className="rounded-2xl shadow-lg w-full h-auto" />
            </motion.div>
            <motion.blockquote initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="lg:col-span-2">
              <motion.div variants={fadeUp}><Quote size={36} className="text-brand-aqua mb-4" /></motion.div>
              <motion.p variants={fadeUp} className="font-heading text-2xl sm:text-3xl text-brand-black leading-snug mb-6">
                &ldquo;Before payments, Sam and I came from hospitality. You learn that people remember how you made them feel. Becoming a B Corp and Living Wage employer is something we&apos;re genuinely proud of — but the real challenge isn&apos;t becoming one. It&apos;s continuing to earn it every day.&rdquo;
              </motion.p>
              <motion.footer variants={fadeUp} className="text-sm font-semibold text-brand-black">Gary Cameron<span className="text-brand-grey font-normal"> — Co-founder, CanDo Payments</span></motion.footer>
            </motion.blockquote>
          </div>
        </div>
      </section>

      {/* This is only the beginning — dark B Corp banner */}
      <section className="py-16 md:py-20 bg-brand-black text-white" data-testid="pbp-beginning">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.div variants={fadeUp} className="flex justify-center mb-8">
              <BCorpMark variant="white" heightClassName="h-28" testId="pbp-beginning-bcorp-mark" />
            </motion.div>
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">This is only the beginning</motion.p>
            {BEGINNING.map((p) => (
              <motion.p key={p.slice(0, 32)} variants={fadeUp} className="text-white/80 leading-relaxed mb-4">{p}</motion.p>
            ))}
            <motion.p variants={fadeUp} className="font-heading text-2xl sm:text-3xl font-bold text-white mt-6">
              Better for business.<br />Better for people.<br />Better for the future.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-20 bg-white" data-testid="pbp-faqs">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">FAQs</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">B Corp &amp; Living Wage, answered</h2>
          </div>
          <ResponsiveAccordion items={FAQS} />
        </div>
      </section>

      {/* At a glance — compact, de-emphasised (SEO parity) */}
      <section className="py-12 bg-gray-50 border-y border-brand-light-grey/50" data-testid="pbp-at-a-glance">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-brand-grey mb-5">At a glance</p>
          <dl className="divide-y divide-brand-light-grey/70">
            {AT_A_GLANCE.map((row) => (
              <div key={row.label} className="grid grid-cols-[104px_1fr] sm:grid-cols-[160px_1fr] gap-x-4 py-3">
                <dt className="text-xs font-bold uppercase tracking-wider text-brand-aqua pt-0.5">{row.label}</dt>
                <dd className="text-sm text-brand-black leading-relaxed">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Final CTA + ContactForm */}
      <section className="py-16 md:py-20 bg-white" data-testid="pbp-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Payments, done properly</h2>
            <p className="text-brand-grey leading-relaxed">Reviewing your provider or comparing costs? We&apos;d be happy to help — transparent advice and real people behind the service.</p>
          </div>
          <div className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
